import { useCallback, useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Button, Typography, Spinner, EmptyState, SettingsCard, SimpleCard } from "@humansignal/ui";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { Form, Input, Label, Toggle } from "../../../components/Form";
import { modal } from "../../../components/Modal/Modal";
import { IconModels, IconExternal } from "@humansignal/icons";
import { useAPI } from "../../../providers/ApiProvider";
import { ProjectContext } from "../../../providers/ProjectProvider";
import { MachineLearningList } from "./MachineLearningList";
import { AudioQCSuggestion } from "./AudioQCSuggestion";
import { CustomBackendForm } from "./Forms";
import { TestRequest } from "./TestRequest";
import { StartModelTraining } from "./StartModelTraining";
import { SettingsPageHeader } from "../../../components/SettingsPageHeader/SettingsPageHeader";
import { cn } from "../../../utils/bem";
import { useTranslation } from "../../../i18n/LocaleProvider";
import "./MachineLearningSettings.prefix.css";

export const MachineLearningSettings = () => {
  const { t } = useTranslation();
  const api = useAPI();
  const { project, fetchProject } = useContext(ProjectContext);
  const [backends, setBackends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Backend choice values keep upstream's "<mode> sampling" suffix;
  // the UI calls Uncertainty "Active learning" (GeneralSettings).
  const samplingIsUncertainty = project?.sampling === "Uncertainty sampling";

  useUpdatePageTitle(createTitleFromSegments([project?.title, "Model Settings"]));

  const fetchBackends = useCallback(async () => {
    setLoading(true);
    const models = await api.callApi("mlBackends", {
      params: {
        project: project.id,
        include_static: true,
      },
    });

    if (models) setBackends(models);
    setLoading(false);
    setLoaded(true);
  }, [project, setBackends]);

  const startTrainingModal = useCallback(
    (backend) => {
      const modalProps = {
        title: "Start Model Training",
        style: { width: 760 },
        closeOnClickOutside: true,
        body: <StartModelTraining backend={backend} />,
      };

      modal(modalProps);
    },
    [project],
  );

  const showRequestModal = useCallback(
    (backend) => {
      const modalProps = {
        title: "Test Request",
        style: { width: 760 },
        closeOnClickOutside: true,
        body: <TestRequest backend={backend} />,
      };

      modal(modalProps);
    },
    [project],
  );

  const showMLFormModal = useCallback(
    (backend) => {
      const action = backend ? "updateMLBackend" : "addMLBackend";
      const modalProps = {
        title: `${backend ? "Edit" : "Connect"} Model`,
        style: { width: 760 },
        closeOnClickOutside: false,
        body: (
          <CustomBackendForm
            action={action}
            backend={backend}
            project={project}
            onSubmit={() => {
              fetchBackends();
              modalRef.close();
            }}
          />
        ),
      };

      const modalRef = modal(modalProps);
    },
    [project, fetchBackends],
  );

  useEffect(() => {
    if (project.id) {
      fetchBackends();
    }
  }, [project.id]);

  return (
    <section>
      <div className={cn("settings-page").toClassName()}>
        <SettingsPageHeader title={t("misc.model")} subtitle={t("misc.connect_an_ml_model_to_generate_live_pre")} />
        {loading && <Spinner size={32} />}
        {loaded && backends.length === 0 && (
          <SimpleCard title="" className="bg-primary-background border-primary-border-subtler p-base">
            <EmptyState
              size="small"
              variant="primary"
              icon={<IconModels />}
              title={t("misc.lets_connect_your_first_model")}
              description={t("misc.connect_a_machine_learning_model_to_gene")}
              actions={
                <Button
                  variant="primary"
                  look="filled"
                  onClick={() => showMLFormModal()}
                  aria-label={t("misc.add_machine_learning_model")}
                >
                  Connect Model
                </Button>
              }
              footer={
                !window.APP_SETTINGS?.whitelabel_is_active && (
                  <Typography variant="label" size="small" className="text-primary-link">
                    <a
                      href="https://afriannotate.waraka.org/docs/projects/ml-backend"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ml-help-link"
                      aria-label={t("misc.learn_more_about_machine_learning_models")}
                      className="inline-flex items-center gap-1 hover:underline"
                    >
                      Learn more
                      <IconExternal width={16} height={16} />
                    </a>
                  </Typography>
                )
              }
            />
          </SimpleCard>
        )}
        {/* M6 — When the project's label_config has <AudioRecord>
            and there's no audio-qc backend registered yet, surface a
            one-click banner to add it. The component below handles
            its own visibility check and POSTs to the same /api/ml
            endpoint MachineLearningList uses. */}
        <AudioQCSuggestion project={project} backends={backends} onAdded={fetchBackends} />
        <MachineLearningList
          onEdit={(backend) => showMLFormModal(backend)}
          onTestRequest={(backend) => showRequestModal(backend)}
          onStartTraining={(backend) => startTrainingModal(backend)}
          fetchBackends={fetchBackends}
          backends={backends}
        />

        {backends.length > 0 && (
          <div className="my-wide">
            <Typography size="small" className="text-neutral-content-subtler">
              A connected model has been detected! If you wish to fetch predictions from this model, please follow these
              steps:
            </Typography>
            <Typography size="small" className="text-neutral-content-subtler mt-base">
              1. Navigate to the <i>Data Manager</i>.
            </Typography>
            <Typography size="small" className="text-neutral-content-subtler mt-tighter">
              2. Select the desired tasks.
            </Typography>
            <Typography size="small" className="text-neutral-content-subtler mt-tighter">
              3. Click on <i>{t("misc.batch_predictions")}</i> {t("misc.from_the")} <i>Actions</i> menu.
            </Typography>
            <Typography size="small" className="text-neutral-content-subtler mt-base">
              If you want to use the model predictions for prelabeling, please configure this in the{" "}
              <NavLink to="annotation" className="hover:underline">
                Annotation settings
              </NavLink>
              .
            </Typography>
          </div>
        )}

        <Form
          action="updateProject"
          formData={{ ...project }}
          params={{ pk: project.id }}
          onSubmit={() => fetchProject()}
        >
          {backends.length > 0 && (
            <div className="p-wide border border-neutral-border rounded-md">
              <Form.Row columnCount={1}>
                <Label text="Configuration" large />

                <div>
                  <Toggle
                    label={t("misc.start_model_training_on_annotation_submi")}
                    description={t("misc.this_option_will_send_a_request_to_train")}
                    name="start_training_on_annotation_update"
                  />
                </div>
              </Form.Row>
            </div>
          )}

          {/* Active-learning loop — retrain-as-you-annotate plus a
              prediction refresh so Uncertainty sampling ranks on fresh
              scores. Only meaningfully enableable with a connected
              backend AND Task sampling set to Active learning; the
              toggle description carries the terse hint otherwise. */}
          {backends.length > 0 && (
            <div className="mt-wide">
              <SettingsCard
                title="Active learning"
                description="Retrain the connected model as annotations arrive, then refresh prediction scores for unlabeled tasks."
              >
                <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                    <Toggle
                      name="active_learning_enabled"
                      disabled={!samplingIsUncertainty}
                      label="Enable active-learning loop"
                      description={
                        samplingIsUncertainty
                          ? `Retrains after every ${project.al_retrain_every ?? 25} new annotations, then refreshes predictions so task ordering follows the new model.`
                          : "Switch Task sampling to Active learning on General settings."
                      }
                    />
                  </div>
                  <div style={{ flex: "0 0 180px" }}>
                    <Input
                      name="al_retrain_every"
                      type="number"
                      min={1}
                      disabled={!samplingIsUncertainty}
                      label="Retrain every"
                      description="New annotations per retrain."
                    />
                  </div>
                </div>
                {project.active_learning_enabled && (
                  <Typography size="small" className="text-neutral-content-subtler">
                    {`Last retrained ${
                      project.al_last_trained_at ? new Date(project.al_last_trained_at).toLocaleString() : "never"
                    }, ${project.al_annotations_since_train ?? 0} annotations since`}
                  </Typography>
                )}
              </SettingsCard>
            </div>
          )}

          {backends.length > 0 && (
            <Form.Actions>
              <Form.Indicator>
                <span case="success">Saved!</span>
              </Form.Indicator>
              <Button
                type="submit"
                variant="primary"
                className="w-[120px]"
                aria-label={t("misc.save_machine_learning_settings")}
              >
                Save
              </Button>
            </Form.Actions>
          )}
        </Form>
      </div>
    </section>
  );
};

MachineLearningSettings.title = "Model";
MachineLearningSettings.path = "/ml";
